import { MapDefinition } from './MapTypes';
import { DefaultMapGenerator } from '../maps/default/DefaultMapGenerator';

const STORAGE_KEY = 'mazingHuntersMaps';
const DEFAULT_MAPS_INITIALIZED_KEY = 'mazingHuntersDefaultMapsInitialized';

function getAllMaps(): MapDefinition[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as MapDefinition[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('[MapStorage] Failed to parse stored maps', error);
    return [];
  }
}

function persistMaps(maps: MapDefinition[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(maps));
}

function cloneMap(map: MapDefinition): MapDefinition {
  return JSON.parse(JSON.stringify(map));
}

export class MapStorage {
  static list(): MapDefinition[] {
    return getAllMaps().map(cloneMap);
  }

  static save(map: MapDefinition): MapDefinition {
    const maps = getAllMaps();
    const existingIndex = maps.findIndex((m) => m.id === map.id);
    const stamped: MapDefinition = {
      ...map,
      metadata: {
        ...map.metadata,
        updatedAt: Date.now(),
      },
    };
    if (existingIndex >= 0) {
      maps[existingIndex] = stamped;
    } else {
      maps.push(stamped);
    }
    persistMaps(maps);
    return cloneMap(stamped);
  }

  static delete(id: string): void {
    const maps = getAllMaps().filter((map) => map.id !== id);
    persistMaps(maps);
  }

  static load(id: string): MapDefinition | null {
    const map = getAllMaps().find((entry) => entry.id === id);
    return map ? cloneMap(map) : null;
  }

  /**
   * Initialize default maps if they don't exist
   */
  static initializeDefaults(): void {
    const initialized = localStorage.getItem(DEFAULT_MAPS_INITIALIZED_KEY);
    if (initialized === 'true') {
      return; // Already initialized
    }

    const defaultMaps = DefaultMapGenerator.generateAll();
    const existingMaps = getAllMaps();
    const existingIds = new Set(existingMaps.map(m => m.id));

    // Only add default maps that don't already exist
    for (const defaultMap of defaultMaps) {
      if (!existingIds.has(defaultMap.id)) {
        existingMaps.push(defaultMap);
      }
    }

    persistMaps(existingMaps);
    localStorage.setItem(DEFAULT_MAPS_INITIALIZED_KEY, 'true');
    console.log(`[MapStorage] Initialized ${defaultMaps.length} default maps`);
  }

  /**
   * Check if a map is a default map
   */
  static isDefaultMap(id: string): boolean {
    return DefaultMapGenerator.isDefaultMap(id);
  }

  /**
   * Get only default maps
   */
  static listDefaults(): MapDefinition[] {
    return getAllMaps()
      .filter(map => DefaultMapGenerator.isDefaultMap(map.id))
      .map(cloneMap);
  }

  /**
   * Get only user-created maps
   */
  static listUserMaps(): MapDefinition[] {
    return getAllMaps()
      .filter(map => !DefaultMapGenerator.isDefaultMap(map.id))
      .map(cloneMap);
  }

  /**
   * Reset default maps (regenerate them)
   */
  static resetDefaults(): void {
    const userMaps = this.listUserMaps();
    const defaultMaps = DefaultMapGenerator.generateAll();
    persistMaps([...userMaps, ...defaultMaps]);
    console.log('[MapStorage] Default maps reset');
  }
}

